import { Explorer } from './Explorer/Explorer';
import { ComponentsList } from './ComponentsList/ComponentsList';
import {
  BuilderToolsDrawerTabValue,
  BuilderTypeEnum,
} from '../../Store/FlowBuilder2PageStore.types';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { Box, Divider, Stack } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ToolsDrawerSection } from './ToolsDrawerSection/ToolsDrawerSection';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { AeSidebarContent } from './AeSidebar/AeSidebarContent';
import { AdvancedEditorItemTypeEnum, makeEditorNavStore, useEditorStore } from '@diana/core';
import { useDragAndDropScroll } from '@diana/core';
import { useBuilderContentHeight } from '../../Support/useBuilderContentHeight';
import { TOOLS_DRAWER_HEADER_HEIGHT } from './ToolsDrawerHeader';

export const ToolsDrawerContent = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const height = useBuilderContentHeight({
    additionalHeight: TOOLS_DRAWER_HEADER_HEIGHT,
    padding: 0,
  });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const contentsRef = useRef<HTMLDivElement | null>(null);

  const [isContentsOpen, setIsContentsOpen] = useState<boolean>(true);
  const [isComponentsOpen, setIsComponentsOpen] = useState<boolean>(true);

  const isLayoutBuilderType = useFlowBuilder2PageStore(
    (s) => s.builderType === BuilderTypeEnum.Nocode,
  );
  const activeTab = useFlowBuilder2PageStore((s) => s.leftDrawerActiveTab);
  const setIsScrollingDndDragItem = useFlowBuilder2PageStore(
    (store) => store.setIsScrollingDndDragItem,
  );

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const resetAeNav = useEditorStore((s) => s.resetAeNav);
  const hasAnyAeItemsOpened = useEditorStore((store) =>
    Object.values(store.aeNav).some((item) => item.level === 0),
  );

  const useEditorNavStore = makeEditorNavStore(flowId);
  const resetLayoutNav = useEditorNavStore((store) => store.resetLayoutNav);
  const hasAnyLayoutItemsOpened = useEditorNavStore((store) =>
    Object.values(store.layoutNav).some((item) => item.level === 0),
  );

  useDragAndDropScroll({
    scrollContainerRef,
    onScrollStart: () => setIsScrollingDndDragItem(true),
    onScrollEnd: () => setIsScrollingDndDragItem(false),
  });

  const disabledComponents = !isLayoutBuilderType;

  const content = useMemo(() => {
    switch (activeTab) {
      case BuilderToolsDrawerTabValue.Explorer:
        return <Explorer />;
      case BuilderToolsDrawerTabValue.Properties:
        return (
          <AeSidebarContent
            keepItemsByType={[
              AdvancedEditorItemTypeEnum.Properties,
              AdvancedEditorItemTypeEnum.CardLayout,
              AdvancedEditorItemTypeEnum.ManageColumns,
              AdvancedEditorItemTypeEnum.Relationships,
              AdvancedEditorItemTypeEnum.Exports,
              AdvancedEditorItemTypeEnum.PrinterContexts,
              AdvancedEditorItemTypeEnum.CustomViews,
            ]}
          />
        );
      case BuilderToolsDrawerTabValue.Rules:
        return (
          <AeSidebarContent
            keepItemsByType={[
              AdvancedEditorItemTypeEnum.Rules,
              AdvancedEditorItemTypeEnum.ChainsGroup,
              AdvancedEditorItemTypeEnum.Activities,
            ]}
          />
        );
      default:
        return null;
    }
  }, [activeTab]);

  const hideCollapseButton = useMemo(() => {
    if (isLayoutBuilderType) {
      return !hasAnyLayoutItemsOpened;
    }

    return !hasAnyAeItemsOpened;
  }, [isLayoutBuilderType, hasAnyLayoutItemsOpened, hasAnyAeItemsOpened]);

  const onContentsToggle = useCallback(() => {
    setIsContentsOpen((prev) => !prev);
  }, []);

  const onComponentsToggle = useCallback(() => {
    setIsComponentsOpen((prev) => !prev);
  }, []);

  const onCloseNavItems = useCallback(() => {
    if (isLayoutBuilderType) {
      return resetLayoutNav();
    }

    resetAeNav();
  }, [isLayoutBuilderType, resetLayoutNav, resetAeNav]);

  const hideContentsHeader = activeTab !== BuilderToolsDrawerTabValue.Explorer;

  return (
    <Stack
      ref={scrollContainerRef}
      height="100%"
      sx={{
        overflow: 'auto',
        overscrollBehavior: 'contain',
        scrollbarWidth: isLayoutBuilderType ? 'none' : 'thin',
      }}
    >
      <Box>
        <Stack justifyContent={disabledComponents ? 'space-between' : 'initial'} minHeight={height}>
          <ToolsDrawerSection
            title={t('titles.contents')}
            isOpen={isContentsOpen}
            onToggle={onContentsToggle}
            onCloseNavItems={onCloseNavItems}
            hideCollapseButton={hideCollapseButton}
            showOnlyContent={hideContentsHeader}
            contentRef={contentsRef}
            contentSx={
              isLayoutBuilderType
                ? {
                    maxHeight: isLayoutBuilderType ? 580 : 'auto',
                    overflow: 'auto',
                    scrollbarWidth: 'thin',
                  }
                : {}
            }
          >
            {content}
          </ToolsDrawerSection>
          <Stack>
            <Divider flexItem />
            <ToolsDrawerSection
              title={t('titles.components')}
              isOpen={isComponentsOpen}
              disabled={disabledComponents}
              onToggle={onComponentsToggle}
            >
              <ComponentsList />
            </ToolsDrawerSection>
            {(!isComponentsOpen || disabledComponents) && <Divider flexItem />}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
